import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSession } from 'next-auth/react';
import { GET_USER_ME, userService } from '@/api';
import { ClientSignDataType } from '../lib/mockDataTypes';

const locationName = (value: unknown): string => {
	if (!value) return '';
	if (typeof value === 'string') return value;
	if (typeof value === 'object' && value !== null && 'name' in value) {
		const name = (value as { name?: unknown }).name;
		return typeof name === 'string' ? name : '';
	}
	return '';
};

export type SettingsLookup = Partial<Record<ClientSignDataType, string>>;

export interface UseClientSettingsLookupResult {
	settings: SettingsLookup;
	isLoading: boolean;
	isError: boolean;
	displayName: string;
	firstName: string;
	lastName: string;
}

export const useClientSettingsLookup = (
	enabled = true
): UseClientSettingsLookupResult => {
	const { data: session } = useSession();
	const { data, isLoading, isError } = useQuery({
		queryKey: [GET_USER_ME],
		queryFn: () => userService.getUserMe(),
		enabled: !!session && enabled,
		staleTime: 1000 * 60 * 5,
	});

	const user = data?.data;
	const firstName = user?.firstname?.trim() ?? '';
	const lastName = user?.lastname?.trim() ?? '';
	const displayName = [firstName, lastName].filter(Boolean).join(' ');
	const city = locationName(user?.city);
	const state = locationName(user?.state);
	const address1 = user?.address1?.trim() ?? '';
	const address2 = user?.address2?.trim() ?? '';
	const zip = user?.zip_code?.trim() ?? '';
	const fullAddress = [address1, address2, city, state, zip]
		.filter(Boolean)
		.join(', ');

	const settings = React.useMemo<SettingsLookup>(() => {
		if (!user) return {};
		return {
			FIRST_NAME: firstName,
			LAST_NAME: lastName,
			FULL_NAME: displayName,
			ADDRESS_1: address1,
			ADDRESS_2: address2,
			FULL_ADDRESS: fullAddress,
			CITY: city,
			STATE: state,
			ZIP_CODE: zip,
			EMAIL: user.email?.trim() ?? '',
			PHONE: user.phonenumber?.trim() ?? '',
		};
	}, [
		user,
		firstName,
		lastName,
		displayName,
		address1,
		address2,
		fullAddress,
		city,
		state,
		zip,
	]);

	return {
		settings,
		isLoading,
		isError,
		displayName,
		firstName,
		lastName,
	};
};
